import React from "react";

import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { trackEvent } from "../../helpers/analyticsHelpers";
import { resolveTwoLetterCode } from "../../helpers/language";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { MenuHeader } from "../settings/MenuHeader";
import {
  Divider,
  InputLabel,
  SettingInput,
  SettingMenu,
  SettingOption
} from "../settings/styles";
import { TopLevelSettingOption } from "../settings/TopLevelSettings";

/*
 * Kuvailutulkkaukselle ei ole omia iso-kielikoodeja, joten mäpätään ne
 * sisääntulevasta iso639-3 -koodista lisäämällä q eteen ja pudottamalla viimeinen kirjain pois.
 * Manifestissa tunnistetut kielet konvertoidaan kuitenkin iso639-1:n mukaisiksi (=2 kirjainta),
 * mutta jos sisään tulee jotain tunnistamatonta päästetään se sellaisenaan läpi.
 */
const audioLangs = {
  ar: "العربية",
  bg: "Български",
  cs: "Čeština",
  da: "Dansk",
  de: "Deutsch",
  el: "Ελληνικά",
  en: "English",
  es: "Español",
  et: "Eesti",
  fa: "فارسی",
  fi: "Suomi",
  fr: "Français",
  he: "עברית",
  hr: "Hrvatski",
  hu: "Magyar",
  is: "Íslenska",
  it: "Italiano",
  ja: "日本語",
  ko: "한국어",
  lt: "Lietuvių",
  lv: "Latviešu",
  mk: "Македонски",
  nl: "Nederlands",
  no: "Norsk",
  pl: "Polski",
  pt: "Português",
  qen: "Kuvailutulkkaus",
  qfi: "Kuvailutulkkaus",
  qsv: "Syntolkning",
  qsw: "Syntolkning",
  ro: "Română",
  ru: "Русский",
  se: "Saame",
  sk: "Slovenčina",
  sl: "Slovenščina",
  so: "Somaali",
  sr: "Српски",
  sv: "Svenska",
  th: "ไทย",
  tr: "Türkçe",
  uk: "Українська",
  zh: "中文"
};

export const languageMap = {
  ara: "ar",
  dan: "da",
  deu: "de",
  eng: "en",
  fin: "fi",
  fra: "fr",
  fre: "fr",
  ger: "de",
  ice: "is",
  isl: "is",
  ita: "it",
  nor: "no",
  por: "pt",
  rus: "ru",
  sma: "se",
  sme: "se",
  smi: "se",
  smn: "se",
  sms: "se",
  som: "so",
  spa: "es",
  swe: "sv",
  ukr: "uk"
};

const audioTrackLabel = track => {
  const lang = languageMap[track.language] ?? track.language;
  if (lang === "fi") {
    if (track.type === "AudioDescription") {
      return "Kuvailutulkkaus";
    }
    if (track.type === "AudioSubtitling") {
      return "Äänitekstitys";
    }
  }
  if (lang === "sv") {
    if (track.type === "AudioDescription") {
      return "Syntolkning";
    }
    if (track.type === "AudioSubtitling") {
      return "Ljudtextning";
    }
  }
  return audioLangs[lang] || `Tuntematon (${lang})`;
};

type OptionsProps = {
  onClickBack: () => void;
  onBlur: (event) => void;
};
export const AudioTracksOptions = ({ onBlur, onClickBack }: OptionsProps) => {
  const { useStoreState } = usePlayerStore();
  const audioTracks = useStoreState.audioTracks();
  const selectedAudioTrackId = useStoreState.selectedAudioTrackId();
  const uiSize = useStoreState.uiSize();
  const vsId = useStoreState.vsId();

  const setUserPreferredAudioTrack = useStoreState.setUserPreferredAudioTrack();

  return (
    <>
      <MenuHeader backToTopLevel={onClickBack} label="aaniraita" />
      <Divider />
      <SettingMenu role="menu">
        {audioTracks?.map((item, i) => (
          <SettingOption key={i + "-" + item.id}>
            <SettingInput
              checked={selectedAudioTrackId === item.id}
              id={`audio-track-setting-${item.id}`}
              name="audio-track"
              onBlur={onBlur}
              onChange={() => {
                trackEvent("areena.player_state.audio_track_changed", {
                  yle_audio_language: item.language,
                  yle_vsid: vsId
                });
                setUserPreferredAudioTrack({
                  language:
                    item.language && resolveTwoLetterCode(item.language),
                  ...(item.type &&
                    item.type !== "AudioTrack" && { type: item.type })
                });
              }}
              onClick={onClickBack}
              value={item.id}
            />
            <InputLabel
              htmlFor={`audio-track-setting-${item.id}`}
              uiSize={uiSize}>
              {audioTrackLabel(item)}
            </InputLabel>
          </SettingOption>
        ))}
      </SettingMenu>
    </>
  );
};

type TopLevelOptionProps = {
  onBlur: (event: React.FocusEvent<HTMLElement>) => void;
  onClick: () => void;
};

export const AudioTracksTopLevelOption = ({
  onBlur,
  onClick
}: TopLevelOptionProps) => {
  const { useStoreState } = usePlayerStore();
  const audioTracks = useStoreState.audioTracks();
  const selectedAudioTrackId = useStoreState.selectedAudioTrackId();
  const { language } = useEmbedParametersContext();
  return (
    <TopLevelSettingOption
      onBlur={onBlur}
      onClick={onClick}
      title={i18n("aaniraita", { language })}
      value={audioTrackLabel(
        audioTracks.find(a => a.id === selectedAudioTrackId) || audioTracks[0]
      )}
    />
  );
};
